import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="ui--copy-button"
//
// Inline copy-to-clipboard trigger, aligned with the @a-safe-digital React
// CopyButton. Copies `textValue` (or the trigger's textContent when no override
// is given), then reveals a "Copied!" popover for `successDelay` ms.
export default class extends Controller {
  static targets = ["trigger", "popover"]
  static values = {
    text: String,
    successDelay: { type: Number, default: 2000 },
  }

  disconnect() {
    this.clearTimeout()
  }

  async copy(event) {
    event.preventDefault()

    const text = this.textToCopy
    if (!text) return

    try {
      await navigator.clipboard.writeText(text)
      this.onSuccess(text)
    } catch (error) {
      // Fallback for browsers/contexts without the async Clipboard API
      if (this.fallbackCopy(text)) {
        this.onSuccess(text)
      } else {
        this.element.dispatchEvent(
          new CustomEvent("copy:error", { bubbles: true, detail: { error } }),
        )
      }
    }
  }

  onSuccess(text) {
    this.showPopover()
    this.element.dispatchEvent(
      new CustomEvent("copy:success", {
        bubbles: true,
        detail: { text },
      }),
    )

    this.clearTimeout()
    this.timeout = setTimeout(() => {
      this.hidePopover()
      this.timeout = null
    }, this.successDelayValue)
  }

  showPopover() {
    if (this.hasPopoverTarget) this.popoverTarget.classList.remove("hidden")
  }

  hidePopover() {
    if (this.hasPopoverTarget) this.popoverTarget.classList.add("hidden")
  }

  clearTimeout() {
    if (this.timeout) {
      clearTimeout(this.timeout)
      this.timeout = null
    }
  }

  get textToCopy() {
    if (this.textValue) return this.textValue
    if (this.hasTriggerTarget) return this.triggerTarget.textContent.trim()
    return ""
  }

  // Returns true on success so callers can show feedback consistently.
  fallbackCopy(text) {
    const textArea = document.createElement("textarea")
    textArea.value = text
    textArea.style.position = "fixed"
    textArea.style.left = "-999999px"
    textArea.style.top = "-999999px"
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    let succeeded = false
    try {
      succeeded = document.execCommand("copy")
    } catch (_error) {
      succeeded = false
    } finally {
      textArea.remove()
    }
    return succeeded
  }
}
